<template>
  <div
    class="microlearning-edit-bar"
    :class="{
      'microlearning-edit-bar--retired': checkpointState === 'retired',
      'microlearning-edit-bar--archived': checkpointState === 'archived',
      'microlearning-edit-bar--static-tabs': isStaticTabState,
    }"
  >
    <div class="tabs">
      <HorizontalMenu
        :key="tabStripKey"
        :items="tabItems"
        :value="activeTabIndex"
        @onTabClick="onTabClick"
        @onRightIconClick="onHistoryClick"
      />
    </div>

    <div v-if="hasActions" class="actions">
      <StandardButton
        v-if="showDiscardButton"
        :label="discardLabel"
        type="magenta"
        variant="text-secondary"
        size="small"
        leftIcon="cornerUpLeft"
        @click="$emit('discard')"
      />

      <template v-if="isSaveAndExitEdit">
        <StandardButton
          label="Save"
          type="magenta"
          variant="primary"
          size="small"
          :disabled="saveDisabled"
          @click="$emit('save')"
        />
        <StandardButton
          label="Exit Edit"
          type="magenta"
          variant="tertiary"
          size="small"
          @click="$emit('exit-edit')"
        />
      </template>

      <template v-else>
        <StandardButton
          v-if="showSecondaryButton"
          :label="secondaryLabel"
          type="magenta"
          :variant="secondaryButtonVariant"
          size="small"
          class="secondary-action-btn"
          @click="onSecondaryClick"
        />

        <EXTooltip
          v-if="showPrimaryButton && activateTooltipEnabled"
          placement="top"
          :title="activateTooltipTitle"
          :delay="200"
          class="microlearning-edit-bar__primary-tooltip"
        >
          <StandardButton
            :label="primaryLabel"
            type="magenta"
            :variant="primaryButtonVariant"
            size="small"
            :disabled="primaryDisabled"
            :class="{ 'unarchive-action-btn': action === 'unarchive' }"
            @click="$emit('primary-action')"
          />
        </EXTooltip>

        <StandardButton
          v-else-if="showPrimaryButton"
          :label="primaryLabel"
          type="magenta"
          :variant="primaryButtonVariant"
          size="small"
          :disabled="primaryDisabled"
          :class="{ 'unarchive-action-btn': action === 'unarchive' }"
          @click="$emit('primary-action')"
        />
      </template>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import HorizontalMenu from "@components/HorizontalMenu/HorizontalMenu.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";

const emit = defineEmits([
  "tab-click",
  "history-click",
  "discard",
  "primary-action",
  "secondary-action",
  "save",
  "exit-edit",
]);

const props = defineProps({
  checkpointState: {
    type: String,
    required: true,
    validator: (v) =>
      [
        "draft",
        "active",
        "active-with-changes",
        "save-article-criteria",
        "retired",
        "archived",
      ].includes(v),
  },
  action: {
    type: String,
    required: true,
    validator: (v) =>
      [
        "no-action",
        "can-save",
        "save-changes",
        "activate",
        "activate-disabled",
        "can-publish",
        "cannot-publish",
        "propose-changes",
        "exit-edit",
        "save-and-exit-edit",
        "reactivate",
        "unarchive",
      ].includes(v),
  },
  /** Disables Save when action is `save-and-exit-edit` (e.g. empty article editor). */
  saveDisabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Which tab is selected. Defaults to Proposed Changes (shown only when
   * `checkpointState` is `active-with-changes`); falls back to the first tab
   * whenever the selected tab is not currently rendered.
   */
  selectedTab: {
    type: String,
    default: "proposed-changes",
    validator: (v) => ["primary", "proposed-changes", "inspections"].includes(v),
  },
  /**
   * MDP-592: renders the Inspections tab after the status tabs. Host-controlled —
   * shown for active checkpoints while the idea-MDP-592 flag is enabled.
   */
  showInspectionsTab: {
    type: Boolean,
    default: false,
  },
  /** Inspection count badge on the Inspections tab (all versions). */
  inspectionsCount: {
    type: [Number, String],
    default: 0,
  },
  /**
   * ER-11249: current published version number. When present for an
   * active / active-with-changes checkpoint, renders a "V{n}" pill immediately
   * to the left of the history icon.
   */
  versionNumber: {
    type: [Number, String],
    default: null,
  },
  /**
   * ER-11208: current live version's publish_message (Rule 12). Rendered in the
   * history-icon hover tooltip when `publishMessageStatus` is READY.
   */
  publishMessage: {
    type: String,
    default: null,
  },
  /**
   * ER-11208: current live version's publish_message_status enum
   * (NOT_APPLICABLE | GENERATING | READY | FAILED). Drives which tooltip variant
   * the history icon shows on hover.
   */
  publishMessageStatus: {
    type: String,
    default: null,
  },
  /**
   * ER-11208: LaunchDarkly gate (`idea-MDP-586`). When false the history icon
   * keeps legacy behavior (no hover tooltip).
   */
  historyTooltipEnabled: {
    type: Boolean,
    default: false,
  },
  /**
   * ER-11860: a pending upload_microlearning_video task exists for this checkpoint.
   * While true, the draft Activate action is disabled with an explanatory tooltip. The
   * draft "Lock to Edit" secondary action remains available (ER-11869) so the assignee can
   * still lock the checkpoint to Cancel/Reassign the pending task and edit Criteria.
   */
  hasPendingUploadTask: {
    type: Boolean,
    default: false,
  },
});

/** Retired / Archived: single non-interactive status tab (no hover, no switching). */
const isStaticTabState = computed(
  () => props.checkpointState === "retired" || props.checkpointState === "archived"
);

const firstTabLabel = computed(() => {
  if (props.checkpointState === "save-article-criteria") return "Draft Article";
  if (props.checkpointState === "active" || props.checkpointState === "active-with-changes")
    return "Active";
  if (props.checkpointState === "retired") return "Retired";
  if (props.checkpointState === "archived") return "Archived";
  return "Draft Checkpoint";
});

const showHistoryIcon = computed(
  () =>
    props.checkpointState === "active" ||
    props.checkpointState === "active-with-changes" ||
    props.checkpointState === "retired" ||
    props.checkpointState === "archived"
);

/** ER-11249: version pill only on Active / Active with Changes, when a version is known. */
const showVersionBadge = computed(
  () =>
    (props.checkpointState === "active" || props.checkpointState === "active-with-changes") &&
    props.versionNumber != null &&
    props.versionNumber !== ""
);

/** Archived has no version history — icon is visible but disabled (~40% opacity). */
const historyIconDisabled = computed(() => props.checkpointState === "archived");

const showProposedChangesTab = computed(() => props.checkpointState === "active-with-changes");

/** Rendered tabs, in order — maps tab index ⇆ `selectedTab` / `tab-click` value. */
const tabKeys = computed(() => {
  const keys = ["primary"];
  if (showProposedChangesTab.value) keys.push("proposed-changes");
  if (props.showInspectionsTab) keys.push("inspections");
  return keys;
});

/**
 * The tab that is actually rendered as selected. `selectedTab` can lag behind
 * (default `proposed-changes` on draft/active, or a hidden Inspections tab) —
 * always resolve to a key in `tabKeys` so HorizontalMenu / Vuetify highlight
 * the visible tab, not a stale index from a previous tab strip.
 */
const resolvedSelectedTab = computed(() => {
  if (tabKeys.value.includes(props.selectedTab)) return props.selectedTab;
  return tabKeys.value[0] || "primary";
});

const activeTabIndex = computed(() => {
  const index = tabKeys.value.indexOf(resolvedSelectedTab.value);
  return index === -1 ? 0 : index;
});

/** Remount Vuetify tabs when the strip changes so the underline tracks the resolved tab. */
const tabStripKey = computed(() => `${tabKeys.value.join("|")}:${resolvedSelectedTab.value}`);

const historySvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none"><path opacity="0.4" d="M18.9166 11.25L17.2505 9.58333L15.5833 11.25M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C12.7516 2.5 15.1571 3.98178 16.4621 6.19091" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 5.83594V10.0026L12.5 11.6693" stroke="currentColor" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

const tabItems = computed(() => {
  const firstItem = {
    title: firstTabLabel.value,
    count: null,
    ...(showHistoryIcon.value
      ? {
          rightIconSvg: historySvg,
          rightIconDisabled: historyIconDisabled.value,
          // ER-11208: hover tooltip content. Archived has no version history
          // (icon disabled) — keep it tooltip-free there. Also gated behind the
          // idea-MDP-586 LaunchDarkly flag (passed in as historyTooltipEnabled).
          ...(!historyIconDisabled.value && props.historyTooltipEnabled
            ? {
                rightIconTooltip: {
                  versionNumber: props.versionNumber,
                  publishMessage: props.publishMessage,
                  publishMessageStatus: props.publishMessageStatus,
                },
              }
            : {}),
        }
      : {}),
    ...(showVersionBadge.value
      ? { statusBadge: `V${props.versionNumber}`, statusBadgeVariant: "count" }
      : {}),
  };
  const items = [firstItem];
  if (showProposedChangesTab.value) items.push({ title: "Proposed Changes", count: null });
  if (props.showInspectionsTab)
    items.push({ title: "Inspections", count: Number(props.inspectionsCount) || 0 });
  return items;
});

const onTabClick = (index) => {
  if (isStaticTabState.value) return;
  emit("tab-click", tabKeys.value[index] || "primary");
};

const onHistoryClick = () => {
  if (historyIconDisabled.value) return;
  emit("history-click");
};

/** Exit Edit uses the dedicated exit-edit event; other secondary actions stay on secondary-action. */
const onSecondaryClick = () => {
  if (props.action === "exit-edit") {
    emit("exit-edit");
    return;
  }
  emit("secondary-action");
};

const isSaveAndExitEdit = computed(() => props.action === "save-and-exit-edit");

const showDiscardButton = computed(() => {
  const { checkpointState, action } = props;
  if (action === "no-action") return checkpointState === "save-article-criteria";
  if (action === "exit-edit" || action === "save-and-exit-edit" || action === "propose-changes")
    return false;
  return [
    "save-changes",
    "activate",
    "activate-disabled",
    "can-save",
    "can-publish",
    "cannot-publish",
  ].includes(action);
});

const discardLabel = computed(() => {
  const { checkpointState, action } = props;
  if (
    checkpointState === "active-with-changes" &&
    ["can-publish", "cannot-publish"].includes(action)
  ) {
    return "Discard proposed changes";
  }
  if (checkpointState === "draft" && ["save-changes", "can-save"].includes(action)) {
    return "Discard changes";
  }
  if (checkpointState === "active-with-changes" && ["can-save", "save-changes"].includes(action)) {
    return "Discard changes";
  }
  return "Discard draft";
});

const showSecondaryButton = computed(() => {
  const { checkpointState, action } = props;
  if (isSaveAndExitEdit.value) return false;
  if (["activate", "activate-disabled"].includes(action) && checkpointState === "draft")
    return true;
  if (action === "exit-edit") return true;
  if (
    ["can-publish", "cannot-publish"].includes(action) &&
    checkpointState === "active-with-changes"
  )
    return true;
  return false;
});

const secondaryLabel = computed(() => {
  const { checkpointState, action } = props;
  if (action === "exit-edit") return "Exit Edit";
  if (["activate", "activate-disabled"].includes(action) && checkpointState === "draft")
    return "Lock to Edit";
  if (
    ["can-publish", "cannot-publish"].includes(action) &&
    checkpointState === "active-with-changes"
  )
    return "Edit";
  return "";
});

/**
 * ER-11596: on a DRAFT the lock-releasing "Exit Edit" is the primary action
 * (purple), not a secondary/outline control. Every other secondary action
 * (Lock to Edit / Edit) keeps the tertiary treatment.
 */
const secondaryButtonVariant = computed(() =>
  props.action === "exit-edit" && props.checkpointState === "draft" ? "primary" : "tertiary"
);

const showPrimaryButton = computed(() => {
  if (isSaveAndExitEdit.value) return false;
  if (props.action === "no-action") return props.checkpointState === "save-article-criteria";
  if (props.action === "exit-edit") return false;
  return true;
});

const primaryDisabled = computed(
  () => props.action === "activate-disabled" || props.action === "cannot-publish"
);

/** ER-11860: explain a disabled Activate when a video upload task is pending. */
const activateTooltipEnabled = computed(
  () => props.hasPendingUploadTask && props.action === "activate-disabled"
);

const activateTooltipTitle = computed(() =>
  "Activate is disabled while a video upload task is pending."
);

/** Figma Unarchive is secondary (white fill + gray border); other actions stay primary. */
const primaryButtonVariant = computed(() => (props.action === "unarchive" ? "outline" : "primary"));

const primaryLabel = computed(() => {
  const { checkpointState, action } = props;
  if (action === "save-changes" || action === "can-save") return "Save Changes";
  if (action === "activate" || action === "activate-disabled") return "Activate";
  if (action === "can-publish" || action === "cannot-publish") return "Publish";
  if (action === "propose-changes") return "Propose Changes";
  if (action === "reactivate") return "Reactivate";
  if (action === "unarchive") return "Unarchive";
  if (action === "no-action" && checkpointState === "save-article-criteria") return "Save Criteria";
  return "";
});

const hasActions = computed(
  () =>
    showDiscardButton.value ||
    showSecondaryButton.value ||
    showPrimaryButton.value ||
    isSaveAndExitEdit.value
);
</script>

<style scoped>
.microlearning-edit-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 12px 0;
  border-bottom: 1px solid #d0d5dd;
}

/* Figma retired: utility-gray-50 — only on RETIRED / ARCHIVED bars */
.microlearning-edit-bar--retired {
  background-color: #f9fafb;
}

/* Figma archived: utility-error-50 */
.microlearning-edit-bar--archived {
  background-color: #fef3f2;
}

.tabs {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: flex-start;
  min-width: 0;
}

.actions {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
}

/* ER-11860: keep the tooltip-wrapped disabled Activate inline with the action bar. */
.microlearning-edit-bar__primary-tooltip {
  display: inline-flex;
}

::v-deep .menu-border .v-slide-group__content.v-tabs-bar__content {
  border-bottom: none !important; /* overrides Vuetify's default tab border which cannot be removed via props */
}

/* ER-11249: version pill is informational — stays gray even when the Active tab is selected
   (HorizontalMenu tints `--count` badges purple on active tabs; Figma wants neutral gray-700). */
::v-deep .microlearning-edit-bar .menu-tabs.v-tab.v-tab--active .menu-tab-status-badge--count {
  color: #344054;
  background-color: #f9fafb;
  border-color: #eaecf0;
}

::v-deep .horizontal-tab .v-tabs-slider-wrapper {
  display: none !important;
}

::v-deep .horizontal-small-tab .menu-tabs.v-tab--active .menu-tab-content {
  position: relative;
}

::v-deep .horizontal-small-tab .menu-tabs.v-tab--active .menu-tab-content::after {
  content: "";
  position: absolute;
  left: -12px;
  right: -12px;
  bottom: -14px;
  height: 2px;
  background-color: #d6bbfb;
}

/* Retired / Archived: kill Vuetify white tab-bar / tab chip. */
.microlearning-edit-bar--static-tabs ::v-deep .v-tabs,
.microlearning-edit-bar--static-tabs ::v-deep .v-tabs-bar,
.microlearning-edit-bar--static-tabs ::v-deep .v-tabs-bar__content,
.microlearning-edit-bar--static-tabs ::v-deep .v-slide-group,
.microlearning-edit-bar--static-tabs ::v-deep .v-slide-group__wrapper,
.microlearning-edit-bar--static-tabs ::v-deep .v-slide-group__content,
.microlearning-edit-bar--static-tabs ::v-deep .menu-tabs.v-tab,
.microlearning-edit-bar--static-tabs ::v-deep .menu-tabs.v-tab:hover,
.microlearning-edit-bar--static-tabs ::v-deep .menu-tabs.v-tab:focus,
.microlearning-edit-bar--static-tabs ::v-deep .menu-tabs.v-tab.v-tab--active {
  background-color: transparent !important;
  background: transparent !important;
  box-shadow: none !important;
}

.microlearning-edit-bar--static-tabs ::v-deep .menu-tabs.v-tab {
  pointer-events: none;
  cursor: default;
}

.microlearning-edit-bar--static-tabs ::v-deep .menu-tabs.v-tab::before,
.microlearning-edit-bar--static-tabs ::v-deep .v-tabs-bar::before {
  display: none !important;
  opacity: 0 !important;
  background-color: transparent !important;
}

::v-deep .standard-button--magenta.standard-button--tertiary.standard-button--default {
  background-color: transparent !important; /* overrides Vuetify scoped style that sets a background on tertiary buttons */
}
::v-deep .standard-button--magenta.standard-button--tertiary.standard-button--default {
  border: 1px solid #d6bbfb !important; /* overrides Vuetify scoped style that sets a border on tertiary buttons */
  color: #6941c6 !important; /* overrides Vuetify scoped style that sets a color on tertiary buttons */
}

/* Figma secondary Unarchive: white fill on tinted bar (outline variant is transparent by default). */
::v-deep .unarchive-action-btn.standard-button--magenta.standard-button--outline {
  background-color: #ffffff !important;
}
</style>
